import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { PaginatedResponse } from '@/types'
import type {
  AdminBalanceTransaction,
  AdminBalanceTransactionFilters,
  AdminContractTransaction,
  AdminContractTransactionFilters,
  AdminPayment,
  AdminPaymentFilters,
} from '@/types/adminFinance'

type ApiResponse<T> = { message?: string; data?: T }

type IndexResponse<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

function unwrapPaginated<T>(raw: IndexResponse<T>): PaginatedResponse<T> {
  return {
    data: raw.data,
    current_page: raw.meta.current_page,
    last_page: raw.meta.last_page,
    per_page: raw.meta.per_page,
    total: raw.meta.total,
  }
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

export const adminFinanceApi = {
  listPayments: async (filters: AdminPaymentFilters = {}): Promise<PaginatedResponse<AdminPayment>> => {
    const response = await apiClient.get<IndexResponse<AdminPayment>>(API_ENDPOINTS.ADMIN.FINANCE.PAYMENTS, {
      params: filters,
    })
    return unwrapPaginated(response.data)
  },

  /** cancelSubscription — деактивировать подписку, оплаченную этим платежом */
  refundPayment: async (
    id: number,
    reason?: string,
    cancelSubscription = false,
  ): Promise<AdminPayment> => {
    const response = await apiClient.post<ApiResponse<AdminPayment>>(
      API_ENDPOINTS.ADMIN.FINANCE.PAYMENT_REFUND(id),
      {
        ...(reason ? { reason } : {}),
        ...(cancelSubscription ? { cancel_subscription: true } : {}),
      },
    )
    return unwrapResource(response.data)
  },

  markPaymentFailed: async (id: number): Promise<AdminPayment> => {
    const response = await apiClient.post<ApiResponse<AdminPayment>>(API_ENDPOINTS.ADMIN.FINANCE.PAYMENT_MARK_FAILED(id))
    return unwrapResource(response.data)
  },

  markPaymentSucceeded: async (id: number): Promise<AdminPayment> => {
    const response = await apiClient.post<ApiResponse<AdminPayment>>(
      API_ENDPOINTS.ADMIN.FINANCE.PAYMENT_MARK_SUCCEEDED(id),
    )
    return unwrapResource(response.data)
  },

  listContractTransactions: async (
    filters: AdminContractTransactionFilters = {},
  ): Promise<PaginatedResponse<AdminContractTransaction>> => {
    const response = await apiClient.get<IndexResponse<AdminContractTransaction>>(
      API_ENDPOINTS.ADMIN.FINANCE.CONTRACT_TRANSACTIONS,
      { params: filters },
    )
    return unwrapPaginated(response.data)
  },

  listBalanceTransactions: async (
    filters: AdminBalanceTransactionFilters = {},
  ): Promise<PaginatedResponse<AdminBalanceTransaction>> => {
    const response = await apiClient.get<IndexResponse<AdminBalanceTransaction>>(
      API_ENDPOINTS.ADMIN.FINANCE.BALANCE_TRANSACTIONS,
      { params: filters },
    )
    return unwrapPaginated(response.data)
  },
}
